import React, { Fragment } from 'react';
import { cn } from '../../lib/classNameHelpers';
import { Button } from '../catalyst-ui-kit/button';

export interface InlineSelectOption<T = string> {
  value: T;
  label?: string;
  hidden?: boolean;
  display?: React.ReactNode;
  disabled?: boolean;
}

type RenderOptionProps<T = string> = {
  isSelected: boolean;
  option: InlineSelectOption<T>;
  defaultProps: {
    role: string;
    title?: string;
    disabled: boolean;
    onClick: () => void;
    'aria-label': string;
    'aria-checked': boolean;
  };
};

interface InlineSelectProps<T = string> {
  options: readonly InlineSelectOption<T>[];
  selected?: T;
  onChange: (value?: InlineSelectOption<T>) => void;
  className?: string;
  activeClassName?: string;
  buttonClassName?: string;
  disabled?: boolean;
  divider?: boolean;
  // Override the title format for each option
  formatTitle?: (option: InlineSelectOption<T>) => string;
  // Optional custom render function for each option
  renderOption?: (props: RenderOptionProps<T>) => React.ReactNode;
}

/** Simple selection component that displays all options in a row. */
export const InlineSelect = <T extends React.Key>({
  options,
  selected,
  onChange,
  disabled,
  className,
  renderOption,
  divider,
  buttonClassName = '!px-2 !py-1',
  activeClassName = 'text-contentPrimary',
  formatTitle = (option) => `Select ${option.label ?? String(option.value)}`,
}: InlineSelectProps<T>) => {
  return (
    <div
      className={cn('flex items-center gap-2', className)}
      role="radiogroup"
      aria-label="Asset selection"
    >
      {options.map((option, index) => {
        if (option.hidden) return null;

        const isSelected = option.value === selected;
        const isDisabled = !!(disabled || option.disabled);

        const defaultProps = {
          role: 'radio',
          disabled: isDisabled,
          'aria-checked': isSelected,
          onClick: () => onChange(option),
          'aria-label': `Select ${option.label}`,
          title: !isSelected ? formatTitle(option) : undefined,
        };

        return (
          <Fragment key={option.value}>
            {renderOption ? (
              renderOption({
                option,
                isSelected,
                defaultProps,
              })
            ) : (
              <Button
                text
                size="xs"
                color="secondary"
                className={cn(buttonClassName, isSelected && activeClassName)}
                {...defaultProps}
              >
                {option.display ?? option.label ?? String(option.value)}
              </Button>
            )}
            {index < options.length - 1 && divider && (
              <div className="h-4 border-r border-borderPrimary" aria-hidden="true" />
            )}
          </Fragment>
        );
      })}
    </div>
  );
};
